import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { MedicationEpisodeVM } from '../vm/types';

import { MedicationRow, type MedicationRowActions } from './MedicationRow';

const COLUMNS = ['Medication', 'Refills', 'Pharmacy', 'Sent by', 'Date', 'Status', ''] as const;

interface MedicationTableProps extends MedicationRowActions {
  episodes: MedicationEpisodeVM[];
  expandedEpisodeIds?: string[];
  emptyLabel: string;
}

const headCellSx = {
  py: 1,
  fontWeight: 600,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  color: 'text.secondary',
  bgcolor: 'backgroundColors.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/** Familiar AMD-style medication grid: fixed columns, one row per medication. */
export const MedicationTable: React.FC<MedicationTableProps> = ({
  episodes,
  expandedEpisodeIds = [],
  emptyLabel,
  onEdit,
  onManageInDoseSpot,
  onDiscontinue,
  onRemove,
  onHistoryToggled,
}) => {
  if (episodes.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    // Narrow screens scroll the table horizontally (BaseBlock parity) instead
    // of crushing or clipping columns.
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 760 }}>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column, index) => (
            <TableCell
              key={column || 'actions'}
              // Refills is numeric — right-aligned with its cells.
              align={
                column === 'Refills' || index === COLUMNS.length - 1 ? 'right' : 'left'
              }
              sx={{
                ...headCellSx,
                width:
                  index === 0 ? '34%' : index === COLUMNS.length - 1 ? 56 : undefined,
              }}
            >
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {episodes.map((episode) => (
          <MedicationRow
            key={episode.id}
            episode={episode}
            defaultExpanded={expandedEpisodeIds.includes(episode.id)}
            onEdit={onEdit}
            onManageInDoseSpot={onManageInDoseSpot}
            onDiscontinue={onDiscontinue}
            onRemove={onRemove}
            onHistoryToggled={onHistoryToggled}
          />
        ))}
      </TableBody>
      </Table>
    </Box>
  );
};
